import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

/* ============================================================
   THÈMES
   ============================================================ */
const THEMES = {
  dark: {
    bgPage:       "#0d0f12",
    bgPanel:      "#1a1d23",
    borderPanel:  "#2e3240",
    bgCard:       "#1f2330",
    borderCard:   "#2e3240",
    bgHeader:     "#252a35",
    textHeader:   "#e8eaed",
    bgLabel:      "#252a35",
    borderLabel:  "#2e3240",
    textLabel:    "#c8cdd8",
    bgInput:      "#1a1d23",
    borderInput:  "#3a3f50",
    textInput:    "#e8eaed",
    bgForm:       "#1a1d23",
    panelTitle:   "#e8eaed",
    bgModal:      "#1f2330",
    borderModal:  "#2e3240",
    textModal:    "#e8eaed",
    btnSaveBg:    "#ffffff",
    btnSaveColor: "#000000",
    btnUpdateBg:  "transparent",
    btnUpdateColor: "#e8eaed",
    btnUpdateBorder: "#3a3f50",
    shadow:       "0 2px 16px rgba(0,0,0,0.5)",
    shadowModal:  "0 10px 40px rgba(0,0,0,0.7)",
  },
  light: {
    bgPage:       "#f0f2f5",
    bgPanel:      "#e2e5ea",
    borderPanel:  "#c9cbd1",
    bgCard:       "#ffffff",
    borderCard:   "#d0d2d8",
    bgHeader:     "#d8d9db",
    textHeader:   "#111111",
    bgLabel:      "#e8eaed",
    borderLabel:  "#d0d0d0",
    textLabel:    "#1a1a1a",
    bgInput:      "#f9fafb",
    borderInput:  "#c0c0c0",
    textInput:    "#111111",
    bgForm:       "#ffffff",
    panelTitle:   "#1a1a1a",
    bgModal:      "#ffffff",
    borderModal:  "#d0d2d8",
    textModal:    "#111111",
    btnSaveBg:    "#111111",
    btnSaveColor: "#ffffff",
    btnUpdateBg:  "transparent",
    btnUpdateColor: "#111111",
    btnUpdateBorder: "#c0c0c0",
    shadow:       "0 2px 12px rgba(0,0,0,0.08)",
    shadowModal:  "0 10px 40px rgba(0,0,0,0.15)",
  },
};

const ACCENT = "#E6FF3E";

/* ============================================================
   STYLES (fonction du thème)
   ============================================================ */
const makeStyles = (t) => ({
  page: {
    minHeight: "100vh",
    background: t.bgPage,
    color: t.textInput,
    fontFamily: 'system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
    transition: "background 0.25s, color 0.25s",
  },
  themeToggle: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px 16px 0",
  },
  themeBtn: {
    background: t.bgHeader,
    color: t.textHeader,
    border: `1px solid ${t.borderCard}`,
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.5px",
    transition: "opacity 0.2s",
  },
  wrap: { padding: "0 16px 36px" },
  panel: {
    background: t.bgPanel,
    border: `1px solid ${t.borderPanel}`,
    padding: 16,
    marginTop: 14,
    borderRadius: 8,
    boxShadow: t.shadow,
  },
  panelTitle: {
    color: t.panelTitle,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 12,
    fontSize: 15,
  },
  card: {
    background: t.bgCard,
    border: `1px solid ${t.borderCard}`,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: t.shadow,
  },
  tableHeader: {
    background: t.bgHeader,
    color: t.textHeader,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    padding: "10px 16px",
    borderBottom: `1px solid ${t.borderCard}`,
    fontSize: 13,
  },
  formAndActions: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
    padding: 16,
  },
  form: {
    background: t.bgForm,
    border: `1px solid ${t.borderCard}`,
    borderRadius: 6,
    overflow: "hidden",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr",
    borderBottom: `1px solid ${t.borderLabel}`,
  },
  cellLabel: {
    background: t.bgLabel,
    color: t.textLabel,
    fontWeight: 700,
    padding: "10px 12px",
    fontSize: 12,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    borderBottom: `1px solid ${t.borderLabel}`,
  },
  cellInput: {
    background: t.bgInput,
    padding: "8px 10px",
  },
  input: {
    width: "100%",
    height: 36,
    borderRadius: 4,
    border: `1px solid ${t.borderInput}`,
    padding: "0 10px",
    fontSize: 14,
    background: t.bgInput,
    color: t.textInput,
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "stretch",
  },
  btnBase: {
    height: 44,
    border: "none",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    cursor: "pointer",
    borderRadius: 6,
    fontSize: 13,
    transition: "opacity 0.2s, transform 0.1s",
  },
  btnSave: {
    background: t.btnSaveBg,
    color: t.btnSaveColor,
  },
  btnPrint: {
    background: ACCENT,
    color: "#000",
  },
  btnUpdate: {
    background: t.btnUpdateBg,
    color: t.btnUpdateColor,
    border: `1.5px solid ${t.btnUpdateBorder}`,
  },
  message: {
    marginTop: 8,
    fontWeight: 700,
    textAlign: "center",
    fontSize: 13,
    whiteSpace: "pre-line",
    padding: 8,
    borderRadius: 4,
  },
  // Modal
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: 20,
    backdropFilter: "blur(3px)",
  },
  modal: {
    width: "100%",
    maxWidth: 360,
    background: t.bgModal,
    color: t.textModal,
    border: `1px solid ${t.borderModal}`,
    borderRadius: 10,
    boxShadow: t.shadowModal,
  },
  modalTitle: {
    fontWeight: 800,
    padding: "14px 16px",
    borderBottom: `1px solid ${t.borderModal}`,
    fontSize: 14,
  },
  modalBody: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "12px 16px",
    borderTop: `1px solid ${t.borderModal}`,
  },
  btnMenu: {
    background: ACCENT,
    color: "#000",
    border: "none",
    padding: "12px 16px",
    fontWeight: 800,
    cursor: "pointer",
    borderRadius: 6,
    fontSize: 14,
    width: "100%",
  },
  btnCancel: {
    background: t.btnSaveBg,
    color: t.btnSaveColor,
    border: "none",
    padding: "10px 16px",
    cursor: "pointer",
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 13,
  },
});

/* ============================================================
   CSS RESPONSIVE injecté une seule fois
   ============================================================ */
if (!document.getElementById("amorceur-responsive")) {
  const s = document.createElement("style");
  s.id = "amorceur-responsive";
  s.innerHTML = `
    @media (min-width: 640px) {
      .af-wrap  { padding: 0 24px 36px !important; }
      .af-row   { grid-template-columns: 180px 1fr !important; }
      .af-label { border-bottom: none !important; border-right: 1px solid var(--af-border) !important; }
    }
    @media (min-width: 900px) {
      .af-wrap { padding: 0 40px 40px !important; }
      .af-fna  { grid-template-columns: 2fr 1fr !important; gap: 20px !important; }
    }
    @media (min-width: 1024px) { .af-wrap { padding: 0 80px 40px !important; } }
    @media (min-width: 1280px) { .af-wrap { padding: 0 120px 40px !important; } }
    @media (min-width: 1536px) { .af-wrap { padding: 0 200px 40px !important; } }
    @media (max-width: 639px) {
      .af-btn   { height: 48px !important; font-size: 13px !important; }
      .af-input { height: 40px !important; font-size: 15px !important; }
    }
    .af-btn:hover:not(:disabled) { opacity: 0.82; transform: translateY(-1px); }
    .af-btn:active:not(:disabled){ transform: translateY(0); }
    .af-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .af-input:focus  { outline: none; border-color: #E6FF3E !important; box-shadow: 0 0 0 3px rgba(230,255,62,0.2); }
    .af-row:last-child { border-bottom: none !important; }
  `;
  document.head.appendChild(s);
}

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AmorceurForm() {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("woodoo-theme") || "dark"
  );
  const t = THEMES[theme];
  const s = makeStyles(t);

  const [options, setOptions]     = useState({ fournisseurs: [], dimamorceur: [] });
  const location                  = useLocation();
  const navigate                  = useNavigate();
  const today                     = new Date().toISOString().split("T")[0];

  const initialFormData = {
    NbSacsLot: "", NumeroSac: "", dimamorceur: "", Fournisseurs: "",
    ReferenceFournisseur: "", NoBatch: "", DatePeremption: "",
    DateReception: today, Conditionnement: "",
    PrixKg: "", PrixSac: "", PrixTotal: "",
    Certif: "", PN: "",
  };

  const [formData, setFormData]     = useState(initialFormData);
  const [editing, setEditing]       = useState(false);
  const [message, setMessage]       = useState("");
  const [messageColor, setMsgColor] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [printing, setPrinting]     = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [certifPdfFile, setCertifPdfFile] = useState(null);
  const [certifPdfKey, setCertifPdfKey]   = useState("");
  const [certifPdfName, setCertifPdfName] = useState("");

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("woodoo-theme", next);
  };

  useEffect(() => {
    (async () => {
      try {
        const [resDim, resFour] = await Promise.all([
          apiFetch("/api/options/dimamorceur"),
          apiFetch("/api/options/fournisseurs"),
        ]);
        if (!resDim.ok || !resFour.ok) throw new Error();
        const [dataDim, dataFour] = await Promise.all([resDim.json(), resFour.json()]);
        setOptions({ fournisseurs: dataFour || [], dimamorceur: dataDim || [] });
      } catch {
        setMessage("❌ Impossible de charger les listes d'options.");
        setMsgColor("#f87171");
      }
    })();
  }, []);

  useEffect(() => {
    const fromState = location.state?.item;
    const fromStorage = !fromState
      ? JSON.parse(localStorage.getItem("woodoo_edit_item") || "null")
      : null;
    const item = fromState || fromStorage;
    if (item) {
      setFormData(p => ({ ...p, ...item }));
      setCertifPdfKey(item.CertifPdfKey || "");
      setCertifPdfName(item.CertifPdfName || "");
      setCertifPdfFile(null);
      setEditing(true);
      localStorage.removeItem("woodoo_edit_item");
    }
  }, [location.state]);

  useEffect(() => {
    if (!formData.DateReception) return;
    const d = new Date(formData.DateReception);
    if (isNaN(d)) return;
    d.setMonth(d.getMonth() + 12);
    const iso = d.toISOString().split("T")[0];
    if (iso !== formData.DatePeremption)
      setFormData(p => ({ ...p, DatePeremption: iso }));
  }, [formData.DateReception]);

  const handleChange = e =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCertifPdfChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setMessage("Seuls les fichiers PDF sont acceptés.");
      setMsgColor("#f87171");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage("PDF trop volumineux (10 Mo max).");
      setMsgColor("#f87171");
      e.target.value = "";
      return;
    }
    setCertifPdfFile(file);
    setCertifPdfName(file.name);
  };

  const extractPnFromMessage = (message) => {
    const m = String(message || "").match(/PN:\s*(\S+)/i);
    return m ? m[1] : "";
  };

  const uploadCertifPdf = async (pn) => {
    if (!certifPdfFile || !pn) return null;
    const fd = new FormData();
    fd.append("pdf", certifPdfFile);
    fd.append("pn", pn);
    const r = await apiFetch("/api/logistique/amorceur/upload-certif", { method: "POST", body: fd });
    const res = await r.json();
    if (!r.ok || res.status !== "success") {
      throw new Error(res.message || "Erreur upload PDF certification");
    }
    return res;
  };

  const openCertifPdf = async () => {
    if (!certifPdfKey) return;
    try {
      const r = await apiFetch(`/api/logistique/amorceur/download-certif?key=${encodeURIComponent(certifPdfKey)}`);
      if (!r.ok) throw new Error("Impossible d'ouvrir le PDF");
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch (e) {
      setMessage(e.message || "Erreur ouverture PDF");
      setMsgColor("#f87171");
    }
  };

  const toYYMM = (val) => {
    if (!val) return "";
    const m = String(val).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? m[1].slice(-2) + m[2] : String(val);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setMessage("");
    try {
      const payload = { ...formData };
      delete payload.CertifPdfKey;
      delete payload.CertifPdfName;
      if (!payload.PN) delete payload.PN;

      const r = await apiFetch(editing ? "/api/amorceur/update" : "/api/amorceur/add", {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const res = await r.json();
      if (res.status !== "success") {
        setMessage(res.message || "Erreur");
        setMsgColor("#f87171");
        return;
      }

      let pn = res.pn || formData.PN || extractPnFromMessage(res.message);
      let pdfKey = certifPdfKey;
      let pdfName = certifPdfName;

      if (certifPdfFile && pn) {
        const up = await uploadCertifPdf(pn);
        pdfKey = up.certif_pdf_key;
        pdfName = up.certif_pdf_name || certifPdfFile.name;
        const r2 = await apiFetch("/api/amorceur/update", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            PN: pn,
            CertifPdfKey: pdfKey,
            CertifPdfName: pdfName,
          }),
        });
        const res2 = await r2.json();
        if (res2.status !== "success") {
          setMessage(res2.message || "PDF uploadé mais enregistrement incomplet");
          setMsgColor("#ffeb99");
        }
      }

      setFormData(prev => ({ ...prev, PN: pn, CertifPdfKey: pdfKey, CertifPdfName: pdfName }));
      setCertifPdfKey(pdfKey);
      setCertifPdfName(pdfName);
      setCertifPdfFile(null);
      if (!editing) setEditing(true);

      setMessage(res.message || "Terminé");
      setMsgColor("#4ade80");
    } catch (e) {
      setMessage(e.message || "Erreur réseau"); setMsgColor("#f87171");
    } finally { setSubmitting(false); }
  };

  const confirmPrint = async () => {
    const pn = [
      formData.dimamorceur, toYYMM(formData.DateReception),
      formData.Fournisseurs, formData.ReferenceFournisseur,
      formData.NoBatch, formData.NbSacsLot, formData.NumeroSac,
    ].filter(Boolean).join("_").toUpperCase();
    try {
      setPrinting(true);
      const res = await apiFetch("/api/amorceur/labels/a4", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pn, n: 2 }),
      });
      if (!res.ok) throw new Error("Erreur d'impression");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
      setMessage("✅ Document prêt à l'impression");
      setMsgColor("#4ade80");
    } catch (e) {
      setMessage(e.message || "Erreur"); setMsgColor("#f87171");
    } finally { setPrinting(false); }
  };

  const inputFields = [
    { name: "ReferenceFournisseur", label: "RÉFÉRENCE FOURNISSEUR" },
    { name: "NoBatch",              label: "N° BATCH" },
    { name: "Conditionnement",      label: "CONDITIONNEMENT (KG)" },
    { name: "NbSacsLot",            label: "NOMBRE DE SACS PAR LOT" },
    { name: "NumeroSac",            label: "NUMÉRO DE SAC" },
  ];

  const priceFields = [
    ["PrixKg",    "PRIX / KG (€)"],
    ["PrixSac",   "PRIX / SAC (€)"],
    ["PrixTotal", "PRIX TOTAL (€)"],
  ];

  return (
    <div style={s.page}>
      <MenuBar active="logistique" />

      {/* Toggle thème */}
      <div style={s.themeToggle}>
        <button style={s.themeBtn} onClick={toggleTheme}>
          {theme === "dark" ? "☀️ Mode clair" : "🌙 Mode sombre"}
        </button>
      </div>

      <div style={s.wrap} className="af-wrap">
        <section style={s.panel}>
          <div style={s.panelTitle}>SUIVI D'AMORCEUR</div>

          <div style={s.card}>
            <div style={s.tableHeader}>PARAMÈTRES</div>

            <div style={s.formAndActions} className="af-fna">
              <form onSubmit={handleSubmit} style={s.form}>

                <Row s={s} label="DATE DE RÉCEPTION">
                  <input type="date" name="DateReception"
                    value={formData.DateReception} onChange={handleChange}
                    style={s.input} className="af-input" />
                </Row>

                <Row s={s} label="DIMENSION AMORCEUR">
                  <select name="dimamorceur" value={formData.dimamorceur}
                    onChange={handleChange} style={s.input} className="af-input">
                    <option value="">-- Sélectionner --</option>
                    {options.dimamorceur.map((o, i) => (
                      <option key={i} value={o.code || o.Nom}>{o.nom || o.Nom}</option>
                    ))}
                  </select>
                </Row>

                <Row s={s} label="FOURNISSEUR">
                  <select name="Fournisseurs" value={formData.Fournisseurs}
                    onChange={handleChange} style={s.input} className="af-input">
                    <option value="">-- Sélectionner --</option>
                    {options.fournisseurs.map((o, i) => (
                      <option key={i} value={o.code || o.Nom}>{o.nom || o.Nom}</option>
                    ))}
                  </select>
                </Row>

                {inputFields.map(({ name, label }) => (
                  <Row key={name} s={s} label={label}>
                    <input name={name} value={formData[name]}
                      onChange={handleChange} style={s.input} className="af-input" />
                  </Row>
                ))}

                <Row s={s} label="DATE DE PÉREMPTION">
                  <input type="date" name="DatePeremption"
                    value={formData.DatePeremption} onChange={handleChange}
                    style={s.input} className="af-input" />
                </Row>

                {priceFields.map(([name, label]) => (
                  <Row key={name} s={s} label={label}>
                    <input type="number" step="0.01" name={name}
                      value={formData[name]} onChange={handleChange}
                      style={s.input} className="af-input" />
                  </Row>
                ))}

                <Row s={s} label="CERTIFICATION">
                  <select name="Certif" value={formData.Certif}
                    onChange={handleChange} style={s.input} className="af-input">
                    <option value="">— Sélectionner —</option>
                    {["FSC", "PEFC", "Bois de France"].map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Row>

                <Row s={s} label="CERTIFICAT PDF">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handleCertifPdfChange}
                      style={{ ...s.input, padding: "6px 10px", height: "auto" }}
                      className="af-input"
                    />
                    {(certifPdfFile || certifPdfKey) && (
                      <div style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", color: t.textInput }}>
                        <span>📄 {certifPdfFile?.name || certifPdfName || "certificat.pdf"}</span>
                        {certifPdfKey && !certifPdfFile && (
                          <button type="button" onClick={openCertifPdf}
                            style={{ background: "transparent", border: `1px solid ${ACCENT}`, color: t.textInput, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                            Voir le PDF
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </Row>
              </form>

              {/* Actions */}
              <div style={s.actions}>
                <button onClick={handleSubmit} disabled={submitting}
                  style={{ ...s.btnBase, ...s.btnSave }} className="af-btn">
                  {submitting ? (editing ? "UPDATING..." : "SAVING...") : (editing ? "UPDATE" : "SAVE")}
                </button>

                <button onClick={confirmPrint} disabled={printing}
                  style={{ ...s.btnBase, ...s.btnPrint }} className="af-btn">
                  {printing ? "PRINTING..." : "PRINT"}
                </button>

                <button onClick={() => setUpdateOpen(true)}
                  style={{ ...s.btnBase, ...s.btnUpdate }} className="af-btn">
                  UPDATE TABLE
                </button>

                {message && (
                  <div style={{ ...s.message, color: messageColor }}>
                    {message}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal */}
          {updateOpen && (
            <div style={s.backdrop} onClick={() => setUpdateOpen(false)}>
              <div style={s.modal} onClick={e => e.stopPropagation()}>
                <div style={s.modalTitle}>Mettre à jour une table d'options</div>
                <div style={s.modalBody}>
                  <button style={s.btnMenu}
                    onClick={() => { setUpdateOpen(false); navigate("/options/dimamorceur"); }}>
                    Amorceur
                  </button>
                  <button style={s.btnMenu}
                    onClick={() => { setUpdateOpen(false); navigate("/options/fournisseurs"); }}>
                    Fournisseurs
                  </button>
                </div>
                <div style={s.modalActions}>
                  <button style={s.btnCancel} onClick={() => setUpdateOpen(false)}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ============================================================
   ROW COMPONENT
   ============================================================ */
function Row({ s, label, children }) {
  return (
    <div style={s.row} className="af-row">
      <div style={s.cellLabel} className="af-label">{label}</div>
      <div style={s.cellInput}>{children}</div>
    </div>
  );
}
